import { FC, useState } from 'react';
import { Form, Upload, UploadFile } from 'antd';
import { useForm } from '@/libs/antd/form';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import { FileData } from '@/libs/fleet-api/services/types';
import { useMutation } from '@tanstack/react-query';
import { uploader } from '@/libs/fleet-api/uploader';

type UploadData = {
	type: 'management' | 'shareholder';
	data?: FileData;
};

export const useUploadModal = (onSubmit: (data: UploadData) => void) => {
	const [modalData, setModalData] = useState<UploadData>();

	return {
		data: modalData,
		isOpen: Boolean(modalData),
		open: (data?: UploadData) => {
			setModalData(data ?? ({} as UploadData));
		},
		close: () => setModalData(undefined),
		onSubmit,
	};
};

const UploadModal: FC<
	ReturnType<typeof useUploadModal> & { prospectId: string; customerId: string }
> = (props) => {
	const { form, fieldName } = useForm<{ file: FileData }>();

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<FleetModal
			open={props.isOpen}
			width={940}
			closable={false}
			okText="SAVE"
			cancelText="BACK"
			onCancel={() => props.close()}
			onOk={form.submit}
			okButtonProps={{
				disabled: uploadMutation.isPending,
			}}
			afterOpenChange={(isOpen) => {
				if (!isOpen) {
					form.resetFields();
				} else if (props.data) {
					form.setFieldsValue({
						file: props.data.data,
					});
				}
			}}
		>
			<FleetTitle>
				Upload Dokumen Akta Perubahan Pengurus dan Pemegang Saham Terakhir
			</FleetTitle>

			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				onFinish={(values) => {
					props.close();
					props.onSubmit({
						data: values.file,
						type: props.data!.type,
					});
				}}
				style={{
					marginTop: 20,
				}}
			>
				<Form.Item
					noStyle
					name={fieldName('file')}
					getValueProps={(value) => {
						const file = value as FileData;
						return {
							fileList: file?.id
								? ([
										{
											uid: file.id,
											name: file.name,
											thumbUrl: file.url,
										},
									] as UploadFile[])
								: [],
						};
					}}
					normalize={({ file }: { file: UploadFile }) => {
						return {
							id: file.uid,
							format: file.type,
							name: file.name,
							url: file.thumbUrl,
						} as FileData;
					}}
				>
					<Upload.Dragger
						listType="picture"
						accept=".jpg,.jpeg,.png"
						maxCount={1}
						beforeUpload={() => false}
						onRemove={() => {
							form.setFieldValue(fieldName('file'), null);
							return false;
						}}
						onChange={async ({ file }) => {
							const fileid = Date.now().toString();
							const filepath = `customer/${props.customerId}/${props.prospectId}/nap`;

							try {
								const url = await uploadMutation.mutateAsync({
									file: file as unknown as File,
									fileid,
									filepath,
								});
								form.setFieldValue(fieldName('file'), {
									format: 'jpeg',
									id: fileid,
									name: file.name,
									path: filepath,
									url,
								});
							} catch {
								form.setFieldValue(fieldName('file'), null);
							}
						}}
					>
						<div
							style={{
								height: 250,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							{uploadMutation.isPending ? (
								<LoadingOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							) : (
								<UploadOutlined
									style={{
										fontSize: 25,
										color: '#BABABA',
										display: 'block',
									}}
								/>
							)}
						</div>
					</Upload.Dragger>
				</Form.Item>
			</Form>
		</FleetModal>
	);
};

export default UploadModal;
